import { Provider, Type } from '@angular/core';
import { Route, ROUTES, Routes } from '@angular/router';

import { BrandMovieService } from '@usheru-hff/movie-detail/feature-brand-movie-conversion-vs-awareness';
import { toBoolean } from '@usheru-hff/sdk/utils';
import { Movie } from '@usheru-hff/shared/domain';

/**
 * Adds new routes to the routes array provided. If there's already a path for a given Route, the
 * existing route is REPLACED by the new ROUTE, otherwise the new route gets added at the beginning of the routes array.
 */
export function addRoute(routes: Routes, newRoutes: Route[], reverse = false) {
  if (routes && routes.length && newRoutes && newRoutes.length) {
    newRoutes.reverse();

    newRoutes.forEach(route => {
      const routeIndex = routes.findIndex(r => r.path === route.path);

      if (routeIndex > -1) {
        routes[routeIndex] = route;
      } else {
        if (reverse) {
          routes.push(route);
        } else {
          routes.unshift(route);
        }
      }
    });
  }

  return routes;
}

export function removeRoute(routes: Routes, ...paths: string[]) {
  if (routes && routes.length && paths && paths.length) {
    paths.forEach(path => {
      const routeIndex = routes.findIndex(r => r.path === path);

      routes.splice(routeIndex, 1);
    });
  }
}

/**
 * Function that will add branded page routing to the website including support for /movie-site/ url path
 *
 * @param routes The routes to be modified by the function
 * @param brandedSlug The slug of the branded page
 * @param brandedComponent The component of the branded page
 */
export function addBrandedRoute(
  routes: Routes,
  brandedSlug: string,
  brandedComponent,
  template?: string,
  extraRouteData?: Record<string, string | boolean>
) {
  addRoute(routes, [
    {
      path: `movie-site/${brandedSlug}`,
      redirectTo: `movie/${brandedSlug}`,
      pathMatch: 'full',
      data: { slug: brandedSlug, ...extraRouteData }
    },
    {
      path: `movie/${brandedSlug}`,
      component: brandedComponent,
      data: { type: `branded-movie`, identifier: `movie-${brandedSlug}`, template: template, ...extraRouteData }
    },
    {
      path: `movie/${brandedSlug}/press-kit`,
      loadChildren: () => import('@usheru-hff/access-locker/assets-locker').then(m => m.AssetsLockerPageModule),
      data: { slug: brandedSlug, ...extraRouteData }
    },
    {
      path: `movie/${brandedSlug}/:section`,
      component: brandedComponent,
      data: { type: `branded-movie`, identifier: `movie-${brandedSlug}`, template: template, ...extraRouteData }
    }
  ]);
}

/**
 * Adds branded-page routes whose component is **lazy-loaded**
 * via Angular’s `loadComponent`.
 *
 * @param routes           Route array to mutate
 * @param brandedSlug      e.g. "batman"
 * @param brandedImporter  () => import('…').then(m => m.FooComponent)
 * @param template         Optional CMS template key
 * @param extraRouteData   Any additional static route data
 */
export function addLazyBrandedRoute(
  routes: Routes,
  brandedSlug: string,
  componentImporterFn: () => Promise<Type<unknown>>,
  template?: string,
  extraRouteData: Record<string, string | boolean> = {}
): void {
  addRoute(routes, [
    {
      path: `movie-site/${brandedSlug}`,
      redirectTo: `movie/${brandedSlug}`,
      pathMatch: 'full',
      data: { slug: brandedSlug, ...extraRouteData }
    },

    {
      path: `movie/${brandedSlug}`,
      loadComponent: () => componentImporterFn(),
      data: {
        type: 'branded-movie',
        identifier: `movie-${brandedSlug}`,
        template,
        ...extraRouteData
      }
    },

    {
      path: `movie/${brandedSlug}/press-kit`,
      loadChildren: () => import('@usheru-hff/access-locker/assets-locker').then(m => m.AssetsLockerPageModule),
      data: { slug: brandedSlug, ...extraRouteData }
    },
    {
      path: `movie/${brandedSlug}/:section`,
      loadComponent: () => componentImporterFn(),
      data: {
        type: 'branded-movie',
        identifier: `movie-${brandedSlug}`,
        template,
        ...extraRouteData
      }
    }
  ]);
}

/**
 * Function that will add branded page routing to the website including support for /movie-site/ url path
 *
 * @param routes The routes to be modified by the function
 * @param brandedSlug The slug of the branded page
 */
export function addAwarnessBrandedPage(routes: Routes, movie: Movie): Route[] {
  const AwarenessModuele = () =>
    import('@usheru-hff/movie-detail/feature-brand-movie-conversion-vs-awareness').then(
      m => m.FeatureMovieAwarenessPageComponent
    );

  const ConversionModule = () =>
    import('@usheru-hff/movie-detail/feature-brand-movie-conversion-vs-awareness').then(
      m => m.FeatureMovieConversionPageComponent
    );

  const movieFriendlyUrl = movie.friendlyUrl;

  const priorityModule = () => {
    const today = new Date().getTime();

    if (isMovieSiteAutomatedSwitch(movie) && movie.locCinemaEnd) {
      return movie.locCinemaEnd > today ? AwarenessModuele : ConversionModule;
    } else {
      return AwarenessModuele;
    }
  };

  return addRoute(routes, [
    {
      path: `movie/${movieFriendlyUrl}`,
      data: { slug: movieFriendlyUrl, identifier: `movie-${movieFriendlyUrl}` },
      providers: [BrandMovieService],
      children: [
        {
          path: '',
          loadComponent: priorityModule()
        },
        {
          path: 'film-info',
          loadComponent: AwarenessModuele
        },
        {
          path: 'book-now',
          loadComponent: ConversionModule
        },
        {
          path: 'watch-now',
          loadComponent: ConversionModule
        }
      ]
    }
  ]);
}

export function addBrandedRouteCustomPath(
  routes: Routes,
  brandedSlug: string,
  brandedComponent,
  path: string,
  template?: string
) {
  addRoute(routes, [
    {
      path: `${path.replace(':slug', brandedSlug)}`,
      component: brandedComponent,
      data: { type: `branded-movie`, identifier: `movie-${brandedSlug}`, template: template }
    },

    {
      path: `${path.replace(':slug', brandedSlug)}/:section`,
      component: brandedComponent,
      data: { type: `branded-movie`, identifier: `movie-${brandedSlug}`, template: template }
    }
  ]);
}

function isMovieSiteAutomatedSwitch(movie: Movie | undefined): boolean {
  return toBoolean(movie?.properties?.movieSiteAutomatedSwitch) || false;
}
